import { FC } from "react"
import { Stack } from "@mui/material"
import {
  TextFieldElement,
  AutocompleteElement,
  useFormContext,
  useWatch,
} from "react-hook-form-mui"
import { RtkHookForm, SecretField } from "@/core/components"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { Userbot } from "../../interfaces"
import { useUpdateClientMutation, useLazyProxiesQuery } from "../../store"
import { ClientPolicy } from "../common/clientPolicy"

interface IProps {
  client: Userbot.Client
  onDone?: () => void
}

interface IFormData {
  name: string
  apiId: number | string
  apiHash: string
  deviceModel: string
  systemVersion: string
  appVersion: string
  langCode: string
  langPack?: string
  proxyId?: number | null
}

const ApiHashField: FC = () => {
  const formCtx = useFormContext<IFormData>()
  const apiHash = useWatch<IFormData>({ name: "apiHash" }) as string
  return (
    <SecretField
      label="apiHash"
      value={apiHash ?? ""}
      onChange={(v) => formCtx.setValue("apiHash", v, { shouldValidate: true })}
      required
      fullWidth
    />
  )
}

export const ClientForm: FC<IProps> = ({ client, onDone }) => {
  const [lazyProxies] = useLazyProxiesQuery()
  const proxiesList = useInfiniteSelectList<Userbot.Proxy>({
    fetchPage: (p) => lazyProxies(p).unwrap(),
    getId: (p) => p.id,
  })

  const defaultValues: IFormData = {
    name: client.name,
    apiId: client.apiId,
    apiHash: client.apiHash,
    deviceModel: client.deviceModel,
    systemVersion: client.systemVersion,
    appVersion: client.appVersion,
    langCode: client.langCode,
    langPack: client.langPack ?? "",
    proxyId: client.proxyId ?? null,
  }

  return (
    <RtkHookForm<IFormData, Userbot.Client>
      successMessage="Клиент обновлён"
      mutationHook={useUpdateClientMutation as never}
      getSubmitHandlerParams={(data) => {
        const body: Userbot.CreateClient = {
          name: data.name,
          apiId: Number(data.apiId),
          apiHash: data.apiHash,
          deviceModel: data.deviceModel,
          systemVersion: data.systemVersion,
          appVersion: data.appVersion,
          langCode: data.langCode,
          langPack: data.langPack || undefined,
          proxyId: data.proxyId == null ? null : Number(data.proxyId),
        }
        return { id: client.id, body }
      }}
      afterSubmit={() => onDone?.()}
      formContextParams={{ defaultValues }}
      actions={[
        {
          text: "Сохранить",
          type: "submit",
          variant: "contained",
          fullWidth: true,
        },
      ]}
    >
      <Stack spacing={2}>
        <ClientPolicy client={client} />
        <TextFieldElement<IFormData> name="name" label="Имя сессии" required />
        <TextFieldElement<IFormData>
          name="apiId"
          label="API ID"
          type="number"
          required
        />
        <ApiHashField />
        <TextFieldElement<IFormData>
          name="deviceModel"
          label="Модель устройства"
          required
        />
        <TextFieldElement<IFormData>
          name="systemVersion"
          label="Версия системы"
          required
        />
        <TextFieldElement<IFormData>
          name="appVersion"
          label="Версия приложения"
          required
        />
        <TextFieldElement<IFormData>
          name="langCode"
          label="Код языка"
          required
        />
        <TextFieldElement<IFormData> name="langPack" label="Lang pack" />
        <AutocompleteElement<IFormData>
          name="proxyId"
          label="Прокси"
          matchId
          loading={proxiesList.isFetching}
          options={proxiesList.items}
          autocompleteProps={{
            fullWidth: true,
            getOptionLabel: (o: Userbot.Proxy) =>
              o ? `${o.label ? o.label + " — " : ""}${o.host}:${o.port}` : "",
            ...infiniteListboxProps({
              hasMore: proxiesList.hasMore,
              isFetching: proxiesList.isFetching,
              onLoadMore: proxiesList.loadMore,
            }),
            renderOption: (props, o: Userbot.Proxy, state) =>
              [
                props,
                `${o.label ? o.label + " — " : ""}${o.host}:${o.port}`,
                state,
              ] as React.ReactNode,
          }}
        />
      </Stack>
    </RtkHookForm>
  )
}
